import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { TEMPLATES } from "@/constants/testIds";
import HintIcon from "@/components/HintIcon";
import { toast } from "sonner";
import { Plus, Save, Trash2, Mail, MessageCircle, X, Sparkles, AlertTriangle, User as UserIcon, RefreshCw, CheckCircle2, Clock as ClockIcon } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import SearchBar, { useLocalFilter } from "@/components/SearchBar";

/** Debounced live preview panel. Calls POST /templates/preview whenever the
 *  body / subject / user-picker changes. */
function PreviewPanel({ body, subject, channel }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(""); // "" = sample user
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Fetch a small pool of trial users for the picker.
    api.get("/trial-users?limit=25").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.users || []);
      setUsers(list.slice(0, 25));
    }).catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const payload = { body: body || "" };
        if (subject) payload.subject = subject;
        if (selected) payload.trial_user_id = selected;
        const r = await api.post("/templates/preview", payload);
        setData(r.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [body, subject, selected]);

  return (
    <div data-testid="template-preview-panel" className="bg-slate-50 border border-slate-200 rounded-md p-4 flex flex-col gap-3 h-full">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-orange-500" /> Live preview
        </div>
        {loading && <div className="text-[10px] text-slate-400 italic">rendering…</div>}
      </div>

      <div>
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1">
          <UserIcon className="h-3 w-3" /> Preview as
        </label>
        <select
          data-testid="template-preview-user-select"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
        >
          <option value="">— Sample: Sibi (NCLEX) —</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.name} · {u.exam || "no-exam"}</option>
          ))}
        </select>
      </div>

      <div className="flex-1 min-h-[180px] flex flex-col gap-2 overflow-hidden">
        {channel === "email" && (
          <div className="text-xs">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Subject</div>
            <div data-testid="template-preview-subject" className="px-2.5 py-1.5 bg-white border border-slate-200 rounded text-slate-800 whitespace-pre-wrap break-words min-h-[28px]">
              {data?.subject_rendered || <span className="italic text-slate-400">(empty)</span>}
            </div>
          </div>
        )}
        <div className="text-xs flex-1 flex flex-col">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Body</div>
          <div
            data-testid="template-preview-body"
            className="flex-1 min-h-[120px] px-2.5 py-2 bg-white border border-slate-200 rounded text-sm text-slate-900 whitespace-pre-wrap break-words overflow-auto"
          >
            {data?.body_rendered || <span className="italic text-slate-400">(empty)</span>}
          </div>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap gap-1.5">
          {(data.used_vars || []).map((v) => (
            <span
              key={`u-${v}`}
              className="inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200"
              title="placeholder resolved"
            >
              {`{{${v}}}`}
            </span>
          ))}
          {(data.empty_vars || []).map((v) => (
            <span
              key={`e-${v}`}
              className="inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
              title="resolved to empty — add a |fallback"
            >
              {`{{${v}}}`}
            </span>
          ))}
          {(data.unknown_vars || []).map((v) => (
            <span
              key={`x-${v}`}
              className="inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-50 text-red-800 border border-red-200"
              title="unknown placeholder — check spelling"
            >
              <AlertTriangle className="h-2.5 w-2.5" />{`{{${v}}}`}
            </span>
          ))}
        </div>
      )}

      {data && data.unknown_vars?.length > 0 && (
        <div className="text-[11px] leading-snug text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          <b>Broken placeholder(s).</b> Template mode will fail-open to AI fallback for cadence sends.
        </div>
      )}
    </div>
  );
}

export default function Templates() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState("");  // template id armed for delete
  // v25 — Meta template cache (fetched from GET /meta-templates)
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [metaHealth, setMetaHealth] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const filtered = useLocalFilter(items, q, ["name", "body", "subject", "description", "channel"]);
  const load = async () => setItems((await api.get("/templates")).data);
  const loadMeta = async () => {
    try {
      const [listRes, healthRes] = await Promise.all([
        api.get("/meta-templates"),
        api.get("/meta-templates/health"),
      ]);
      setMetaTemplates(listRes.data || []);
      setMetaHealth(healthRes.data || null);
    } catch { /* silent — fresh env or no permission */ }
  };
  useEffect(() => { load(); loadMeta(); }, []);

  // v25 — When opening an existing WhatsApp template with a Meta mapping but
  // empty/mismatched meta_body_params, auto-populate the slot skeleton from
  // Meta's synced components. Prevents Meta 132000 at send time and gives ops
  // a one-shot 'pick source per slot' fix for legacy misconfigured rows.
  useEffect(() => {
    if (!editing || editing.channel !== "whatsapp" || !editing.meta_template_name) return;
    if (metaTemplates.length === 0) return;
    const lang = editing.meta_language || "en";
    const found = metaTemplates.find(
      (mt) => mt.name === editing.meta_template_name && mt.language === lang,
    );
    if (!found) return;
    const bodyText = ((found.components || []).find(
      (c) => (c.type || "").toUpperCase() === "BODY",
    ) || {}).text || "";
    const matches = bodyText.match(/\{\{([^}]+)\}\}/g) || [];
    const expected = matches.length;
    const current = (editing.meta_body_params || []).length;
    if (expected === current) return;  // already in sync
    const slots = matches.map((m) => {
      const inner = m.slice(2, -2).trim();
      const isPositional = /^\d+$/.test(inner);
      return { name: isPositional ? "" : inner, source: "first_name" };
    });
    // Preserve existing user selections by index/name where possible
    const prev = editing.meta_body_params || [];
    const merged = slots.map((s, i) => {
      const match = s.name ? prev.find((p) => p.name === s.name) : prev[i];
      return match?.source ? { ...s, source: match.source } : s;
    });
    setEditing((e) => ({ ...e, meta_body_params: merged }));
  }, [editing?.id, editing?.meta_template_name, editing?.meta_language, metaTemplates.length]);

  const syncMetaTemplates = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/meta-templates/sync");
      toast.success(`Synced ${r.data.synced_count} templates from Meta`);
      loadMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const save = async () => {
    if (!editing.name.trim() || !editing.body.trim()) return toast.error("Name and body required");
    // v25 — client-side pre-flight: block save if the selected Meta template is
    // not APPROVED (mirrors backend so user sees an inline error before POST).
    if (editing.channel === "whatsapp" && editing.meta_template_name && metaTemplates.length > 0) {
      const lang = editing.meta_language || "en";
      const found = metaTemplates.find(
        (mt) => mt.name === editing.meta_template_name && mt.language === lang,
      );
      if (!found) {
        return toast.error(`Meta template '${editing.meta_template_name}' (${lang}) not in synced list. Click Sync Meta Templates.`);
      }
      if (found.status !== "APPROVED") {
        return toast.error(`Meta template '${editing.meta_template_name}' is ${found.status}. Only APPROVED templates can be used.`);
      }
      // Body variable slot count MUST match Meta template — else Meta 132000.
      const body = ((found.components || []).find(
        (c) => (c.type || "").toUpperCase() === "BODY",
      ) || {}).text || "";
      const expectedSlots = (body.match(/\{\{[^}]+\}\}/g) || []).length;
      const provided = (editing.meta_body_params || []).length;
      if (expectedSlots !== provided) {
        return toast.error(`Meta template needs ${expectedSlots} body variable${expectedSlots === 1 ? "" : "s"}, but ${provided} configured. Fix in 'Body variables' below.`);
      }
      const missing = (editing.meta_body_params || []).find((p) => !p.source);
      if (missing) {
        return toast.error("Every body variable must have a User attribute source. See 'Body variables' below.");
      }
    }
    try {
      await api.post("/templates", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  // Two-click confirm — first click arms, second within 5s deletes.
  // Native confirm() is silently blocked by the preview iframe.
  const remove = async (id) => {
    if (confirmingDelete !== id) {
      setConfirmingDelete(id);
      setTimeout(() => setConfirmingDelete((cur) => (cur === id ? "" : cur)), 5000);
      return;
    }
    setConfirmingDelete("");
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  // Memoize preview-relevant slice so the panel doesn't re-render on unrelated edits.
  const previewProps = useMemo(() => ({
    body: editing?.body || "",
    subject: editing?.channel === "email" ? (editing?.subject || "") : "",
    channel: editing?.channel || "whatsapp",
  }), [editing?.body, editing?.subject, editing?.channel]);

  return (
    <div data-testid={TEMPLATES.root} className="p-8 lg:p-12 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Message Library</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            Templates
            <HintIcon
              testId="templates-page-hint"
              hint={
                <>
                  Reusable message templates for WhatsApp &amp; Email. Cadence steps and manual sends
                  can pick a template as a starting point.<br /><br />
                  Placeholders auto-fill from the user record:<br />
                  <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">{"{{first_name|there}}"}</code>{" · "}
                  <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">{"{{exam}}"}</code>{" · "}
                  <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">{"{{days_since_signup}}"}</code>{" · "}
                  <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">{"{{trial_days_left}}"}</code>{" · "}
                  <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">{"{{last_login}}"}</code>
                </>
              }
            />
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={syncMetaTemplates}
            disabled={syncing}
            className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-700 text-xs font-semibold px-3 py-2 rounded-md hover:bg-slate-50 disabled:opacity-50"
            data-testid="templates-sync-meta-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Meta Templates"}
          </button>
          <ExportButton path="/exports/templates.xlsx" filename="templates.xlsx" testId="templates-export-btn" />
          <button
            data-testid={TEMPLATES.addBtn}
            onClick={() => setEditing({ name: "", channel: "whatsapp", subject: "", body: "", description: "" })}
            className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150"
          >
            <Plus className="h-3.5 w-3.5" /> Add template
          </button>
        </div>
      </div>

      {/* Meta HSM sync status strip */}
      {metaHealth && (
        <div className="mb-4 flex items-center gap-3 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5" data-testid="meta-templates-status-strip">
          <span className={`inline-block h-2 w-2 rounded-full ${metaHealth.ok ? "bg-emerald-500" : metaHealth.last_synced_at ? "bg-amber-500" : "bg-slate-300"}`} />
          <span>{metaHealth.detail}</span>
          {metaHealth.last_synced_at && (
            <span className="text-slate-400">· last synced {new Date(metaHealth.last_synced_at).toLocaleString()}</span>
          )}
          {metaHealth.problems_count > 0 && (
            <span className="ml-auto text-red-600 font-semibold">
              {metaHealth.problems_count} template{metaHealth.problems_count === 1 ? "" : "s"} need attention
            </span>
          )}
        </div>
      )}

      <div className="mb-4"><SearchBar value={q} onChange={setQ} placeholder="Search templates…" testId="templates-search" /></div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((t) => (
          <div key={t.id} data-testid={`${TEMPLATES.itemPrefix}${t.id}`} className="bg-white border border-slate-200 rounded-lg p-5 group">
            <div className="flex items-center justify-between mb-2">
              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${
                t.channel === "whatsapp" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-indigo-50 text-indigo-700 border-indigo-200"
              }`}>
                {t.channel === "whatsapp" ? <MessageCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                {t.channel}
              </span>
              <div className={`flex gap-2 transition-opacity duration-150 ${confirmingDelete === t.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                <button onClick={() => setEditing(t)} className="text-xs font-semibold text-slate-700 hover:text-orange-600">Edit</button>
                <button
                  onClick={() => remove(t.id)}
                  className={`text-xs font-semibold inline-flex items-center gap-1 transition-colors duration-150 ${confirmingDelete === t.id ? "text-red-600" : "text-slate-400 hover:text-red-600"}`}
                  title={confirmingDelete === t.id ? "Click again to confirm" : "Delete this template"}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {confirmingDelete === t.id && <span>Confirm</span>}
                </button>
              </div>
            </div>
            <h3 className="font-heading font-bold text-base tracking-tight">{t.name}</h3>
            {t.subject && <div className="text-xs text-slate-500 mt-1">Subject: {t.subject}</div>}
            <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap line-clamp-4">{t.body}</p>
            {t.description && <p className="text-xs text-slate-500 mt-2 italic">{t.description}</p>}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-5xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg tracking-tight">{editing.id ? "Edit template" : "New template"}</h3>
              <button onClick={() => setEditing(null)}><X className="h-4 w-4 text-slate-500" /></button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* LEFT — form */}
              <div className="space-y-3">
                <input placeholder="Template name" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none" />
                <div className="flex gap-2">
                  <select value={editing.channel}
                    onChange={(e) => setEditing({ ...editing, channel: e.target.value })}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none">
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                  </select>
                  {editing.channel === "email" && (
                    <input placeholder="Subject" value={editing.subject || ""}
                      onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none" />
                  )}
                </div>
                <textarea rows={10} placeholder="Message body — use {{first_name|there}}, {{exam}}, {{days_since_signup}}, {{trial_days_left}}, {{last_login}}"
                  value={editing.body}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono" />
                <input placeholder="Description (internal)" value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none" />

                {editing.channel === "whatsapp" && (
                  <div className="mt-2 border-t border-slate-200 pt-3">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">
                      Meta HSM (required for cold sends)
                    </div>
                    {metaTemplates.length === 0 ? (
                      <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                        No Meta templates synced yet. Click <span className="font-semibold">Sync Meta Templates</span> at the top of this page to fetch approved templates from Meta Business Manager.
                        You can still save this template as freeform (inside 24-hour window only).
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">
                        Pick an approved Meta template from the dropdown below. To send outside the 24-hour customer service window, you MUST select one. Freeform (no selection) only works when the user replied in the last 24 hours.
                      </p>
                    )}
                    <div className="grid grid-cols-1 gap-2">
                      <select
                        data-testid="template-meta-name"
                        value={editing.meta_template_name && editing.meta_language
                          ? `${editing.meta_template_name}::${editing.meta_language}` : ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) {
                            setEditing({ ...editing, meta_template_name: "", meta_language: "" });
                            return;
                          }
                          const [name, lang] = val.split("::");
                          // Auto-populate meta_body_params from the Meta template's
                          // components. Parses {{name}} and {{1}} slots out of the
                          // approved body text so ops just picks the user-attribute
                          // source for each row. Prevents Meta error 132000
                          // (parameter count mismatch).
                          const picked = metaTemplates.find(
                            (mt) => mt.name === name && mt.language === lang,
                          );
                          const body = ((picked?.components || []).find(
                            (c) => (c.type || "").toUpperCase() === "BODY",
                          ) || {}).text || "";
                          const matches = body.match(/\{\{([^}]+)\}\}/g) || [];
                          const slots = matches.map((m) => {
                            const inner = m.slice(2, -2).trim();
                            // If inner is purely numeric it's positional ({{1}}),
                            // otherwise it's a named variable ({{name}}).
                            const isPositional = /^\d+$/.test(inner);
                            return {
                              name: isPositional ? "" : inner,
                              source: "first_name",
                            };
                          });
                          // Preserve any user-picked sources when re-selecting the
                          // same template. Match by slot name (named) or by index.
                          const prev = editing.meta_body_params || [];
                          const merged = slots.map((s, i) => {
                            const match = s.name
                              ? prev.find((p) => p.name === s.name)
                              : prev[i];
                            return match?.source ? { ...s, source: match.source } : s;
                          });
                          setEditing({
                            ...editing,
                            meta_template_name: name,
                            meta_language: lang,
                            meta_body_params: merged,
                          });
                        }}
                        className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                      >
                        <option value="">
                          {metaTemplates.length === 0 ? "Freeform only (no Meta template)" : "— Freeform / no Meta template —"}
                        </option>
                        {metaTemplates.map((mt) => (
                          <option
                            key={`${mt.name}-${mt.language}`}
                            value={`${mt.name}::${mt.language}`}
                            disabled={mt.status !== "APPROVED"}
                          >
                            {mt.status === "APPROVED" ? "✓" : mt.status === "PENDING" ? "…" : "✗"}
                            {" "}{mt.name} ({mt.language}) · {mt.category} · {mt.status}
                          </option>
                        ))}
                      </select>
                      {/* Selected template card + category hint */}
                      {editing.meta_template_name && (() => {
                        const sel = metaTemplates.find(
                          (mt) => mt.name === editing.meta_template_name && mt.language === (editing.meta_language || "en"),
                        );
                        if (!sel) return (
                          <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
                            <AlertTriangle className="inline h-3 w-3 mr-1" />
                            <span className="font-mono">{editing.meta_template_name}</span> ({editing.meta_language || "en"}) is not in the synced Meta list. Click Sync Meta Templates.
                          </div>
                        );
                        const catHint = {
                          UTILITY: {
                            color: "border-emerald-200 bg-emerald-50 text-emerald-800",
                            icon: <CheckCircle2 className="h-3.5 w-3.5" />,
                            title: "Utility",
                            text: "Account updates and trial notifications. Can be sent outside the 24-hour window.",
                          },
                          MARKETING: {
                            color: "border-amber-200 bg-amber-50 text-amber-800",
                            icon: <AlertTriangle className="h-3.5 w-3.5" />,
                            title: "Marketing",
                            text: "Promotions and subscription offers. Delivery depends on Meta&apos;s policies and user opt-in.",
                          },
                          AUTHENTICATION: {
                            color: "border-blue-200 bg-blue-50 text-blue-800",
                            icon: <ClockIcon className="h-3.5 w-3.5" />,
                            title: "Authentication",
                            text: "OTP / 2FA only. Not typically used for trial conversion.",
                          },
                        }[sel.category] || {
                          color: "border-slate-200 bg-slate-50 text-slate-700",
                          icon: <ClockIcon className="h-3.5 w-3.5" />,
                          title: sel.category || "Category",
                          text: "",
                        };
                        return (
                          <div className={`text-[11px] border rounded p-2 ${catHint.color}`} data-testid="meta-template-hint">
                            <div className="flex items-center gap-1.5 font-semibold mb-0.5">
                              {catHint.icon}
                              <span>{catHint.title}</span>
                              {sel.status !== "APPROVED" && (
                                <span className="ml-1 text-red-600">· {sel.status}</span>
                              )}
                            </div>
                            <div className="opacity-80">{catHint.text}</div>
                          </div>
                        );
                      })()}
                      {!editing.meta_template_name && (
                        <div className="text-[11px] border border-blue-200 bg-blue-50 text-blue-800 rounded p-2">
                          <div className="flex items-center gap-1.5 font-semibold mb-0.5">
                            <ClockIcon className="h-3.5 w-3.5" />
                            Free-form message
                          </div>
                          <div className="opacity-80">Can only be sent if the user has replied within the last 24 hours.</div>
                        </div>
                      )}
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                          Body variables (in order they appear in the Meta template)
                        </div>
                        <button
                          type="button"
                          data-testid="template-meta-param-add"
                          onClick={() => setEditing({
                            ...editing,
                            meta_body_params: [...(editing.meta_body_params || []), { name: "", source: "first_name" }],
                          })}
                          className="text-[11px] font-semibold text-orange-600 hover:text-orange-500"
                        >
                          + Add variable
                        </button>
                      </div>
                      {(editing.meta_body_params || []).length === 0 && (
                        <div className="text-[11px] italic text-slate-400 border border-dashed border-slate-200 rounded-md px-2 py-2 bg-slate-50">
                          No variables. Add one row per <code className="font-mono">{"{{var}}"}</code> in your Meta template.
                        </div>
                      )}
                      {(editing.meta_body_params || []).map((p, i) => (
                        <div key={i} className="flex gap-2 mt-1.5" data-testid={`template-meta-param-${i}`}>
                          <input
                            placeholder="Meta var name (e.g. name) — leave blank for positional {{1}}"
                            value={p.name || ""}
                            onChange={(e) => {
                              const next = [...(editing.meta_body_params || [])];
                              next[i] = { ...next[i], name: e.target.value };
                              setEditing({ ...editing, meta_body_params: next });
                            }}
                            className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono" />
                          <select
                            value={p.source || "first_name"}
                            onChange={(e) => {
                              const next = [...(editing.meta_body_params || [])];
                              next[i] = { ...next[i], source: e.target.value };
                              setEditing({ ...editing, meta_body_params: next });
                            }}
                            className="px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                          >
                            <option value="first_name">first_name</option>
                            <option value="name">name (full)</option>
                            <option value="exam">exam</option>
                            <option value="city">city</option>
                            <option value="state">state</option>
                            <option value="country">country</option>
                            <option value="trial_days_left">trial_days_left</option>
                            <option value="trial_hours_left">trial_hours_left</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => {
                              const next = [...(editing.meta_body_params || [])];
                              next.splice(i, 1);
                              setEditing({ ...editing, meta_body_params: next });
                            }}
                            className="text-slate-400 hover:text-red-600 px-1"
                            aria-label="Remove variable"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* RIGHT — live preview */}
              <PreviewPanel {...previewProps} />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button data-testid={TEMPLATES.saveBtn} onClick={save} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-500 transition-colors duration-150">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
